import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'

import { AuthScaffold } from './AuthScaffold'
import { resendErrorMessage, tokenErrorMessage } from './authErrors'
import { useAuth } from '@/auth/AuthProvider'
import { FxAlert, FxButton, FxIcon, FxSpinner } from '@/components/fx'
import { apiClient, authApi, skipAuthRefresh } from '@/lib/api'

type Phase = 'waiting' | 'confirming' | 'confirmed' | 'failed'

/**
 * The confirmation screen, in the two states it can honestly be in: waiting
 * for the click in the inbox, and confirmed.
 *
 * It answers on two paths. `/verify-email` is where registration lands
 * someone who has no form waiting; `/auth/verify-email?token=…` is the link
 * in the mail itself (`internal/auth/service.go` builds it from
 * `frontendURL`), and that one consumes the token.
 *
 * Confirming does not require a session: the route is on the gateway's
 * public skip-list, so the link works in whatever browser the mail was
 * opened in.
 */
export function VerifyEmailPage() {
  const [params] = useSearchParams()
  const { user, signedIn, reload } = useAuth()
  const state = useLocation().state as { email?: string; signInFailed?: string } | null

  const token = params.get('token')
  const email = state?.email ?? user?.email
  const [phase, setPhase] = useState<Phase>(() =>
    token ? 'confirming' : user?.emailVerified ? 'confirmed' : 'waiting',
  )
  const [tokenError, setTokenError] = useState<string | null>(null)
  const [resendState, setResendState] = useState<'idle' | 'busy' | 'sent'>('idle')
  const [resendError, setResendError] = useState<string | null>(null)
  const [checked, setChecked] = useState(false)
  // The token is single-use; React 18's StrictMode runs effects twice in
  // development and the second attempt on a consumed token would report a
  // failure on a confirmation that worked.
  const consumed = useRef(false)

  useEffect(() => {
    if (!token || consumed.current) return
    consumed.current = true
    void (async () => {
      try {
        await apiClient.axios.post('/v1/auth/verify-email', { token }, skipAuthRefresh)
        setPhase('confirmed')
        // The session's own view of the account is now stale; the upload
        // pause elsewhere in the app reads it.
        if (signedIn) await reload()
      } catch (cause) {
        setTokenError(tokenErrorMessage(cause))
        setPhase('failed')
      }
    })()
  }, [token, signedIn, reload])

  // A person who confirmed in another tab comes back to this one and presses
  // "Ich habe bestätigt"; nothing else would notice.
  useEffect(() => {
    if (phase === 'waiting' && user?.emailVerified) setPhase('confirmed')
  }, [phase, user?.emailVerified])

  if (phase === 'confirming') {
    return (
      <AuthScaffold title="E-Mail wird bestätigt">
        <FxSpinner label="Bestätigung wird geprüft …" />
      </AuthScaffold>
    )
  }

  if (phase === 'confirmed') {
    return (
      <AuthScaffold
        title="E-Mail bestätigt"
        footer={
          signedIn ? undefined : (
            <p className="text-small">
              <Link className="text-primary-text" to="/login">
                Zur Anmeldung →
              </Link>
            </p>
          )
        }
      >
        <FxAlert tone="success" className="text-left">
          {email ? <strong>{email}</strong> : 'Deine Adresse'} ist bestätigt. Alles freigeschaltet.
        </FxAlert>
        {signedIn && (
          <FxButton asChild block>
            <Link to="/">Weiter zu deinen Formularen</Link>
          </FxButton>
        )}
      </AuthScaffold>
    )
  }

  return (
    <AuthScaffold
      title="E-Mail bestätigen"
      footer={
        signedIn ? undefined : (
          <p className="text-small">
            <Link className="text-primary-text" to="/login">
              Zur Anmeldung →
            </Link>
          </p>
        )
      }
    >
      <FxIcon name="mark_email_unread" size={56} className="self-center text-primary" />

      <p className="text-body text-text">
        Wir haben einen Bestätigungslink an{' '}
        {email ? <strong>{email}</strong> : 'deine E-Mail-Adresse'} geschickt. Klicke auf den Link,
        dann ist alles freigeschaltet.
      </p>

      {phase === 'failed' && tokenError && (
        <FxAlert tone="danger" className="text-left">
          {tokenError}
        </FxAlert>
      )}

      {state?.signInFailed && (
        <FxAlert tone="warning" title="Konto angelegt, Anmeldung offen" className="text-left">
          {state.signInFailed}
        </FxAlert>
      )}

      {resendState === 'sent' && (
        <FxAlert tone="success" className="text-left">
          Wir haben die E-Mail noch einmal geschickt.
        </FxAlert>
      )}
      {resendError && (
        <FxAlert tone="danger" className="text-left">
          {resendError}
        </FxAlert>
      )}
      {checked && (
        <FxAlert tone="warning" className="text-left">
          Noch nicht bestätigt. Schau bitte auch im Spam-Ordner nach.
        </FxAlert>
      )}

      {signedIn ? (
        <>
          <FxButton
            block
            disabled={resendState === 'busy'}
            onClick={() => {
              setResendState('busy')
              setResendError(null)
              void authApi
                .resendVerification()
                .then(() => setResendState('sent'))
                .catch((cause) => {
                  setResendError(resendErrorMessage(cause))
                  setResendState('idle')
                })
            }}
          >
            {resendState === 'busy' ? 'E-Mail wird gesendet …' : 'E-Mail erneut senden'}
          </FxButton>
          <FxButton
            variant="outlined"
            block
            onClick={() => {
              setChecked(false)
              void reload().then(() => setChecked(true))
            }}
          >
            Ich habe bestätigt
          </FxButton>
          {/* The account works while the confirmation is outstanding — the
              gateway only pauses new uploads once its grace window has run
              out — so there is no reason to hold anyone on this screen. */}
          <FxButton asChild variant="outlined" block>
            <Link to="/">Später bestätigen</Link>
          </FxButton>
        </>
      ) : (
        <p className="text-small text-text-muted">
          Die E-Mail lässt sich erneut senden, sobald du angemeldet bist.
        </p>
      )}
    </AuthScaffold>
  )
}
